import type {
  OfflineAuthMethods,
  OfflineSnapshot,
  OfflineUserSummary,
} from "@observe-tech/utils";

const SNAPSHOT_FRESH_MS = 24 * 60 * 60 * 1000;

const OFFLINE_METHOD_NAMES = [
  "getOfflineSession",
  "setOfflineSession",
  "listOfflineUsers",
  "clearOfflineSession",
  "isOfflineStorageAvailable",
] as const satisfies readonly (keyof OfflineAuthMethods)[];

// The one window cast for the offline-auth bridge, defined once. Partial, not
// OfflineAuthMethods: a desktop build older than this feature still exposes
// window.electronAPI, so the object being present says nothing about these
// five methods existing on it.
function getBridge(): Partial<OfflineAuthMethods> | undefined {
  return (window as unknown as { electronAPI?: Partial<OfflineAuthMethods> })
    .electronAPI;
}

// Presence of the bridge, nothing more. Whether safeStorage can actually
// encrypt is a main-process question and only isOfflineStorageAvailable can
// answer it, which is why that one is async and this one is not.
export function isOfflineCapable(): boolean {
  const bridge = getBridge();
  if (!bridge) return false;
  return OFFLINE_METHOD_NAMES.every(
    (name) => typeof bridge[name] === "function",
  );
}

// Every call below funnels through here. A bridge that is missing, half-built
// or throwing must degrade to a safe empty value and never throw into a boot
// path — a session lost to an IPC error is the class of failure this whole
// feature exists to remove.
//
// Degrading quietly is not the same as failing quietly: a site that refuses
// offline login has to leave something to read. A throw is warned about; an
// absent bridge is not, because that is every browser build behaving normally.
async function call<T>(
  method: keyof OfflineAuthMethods,
  invoke: (bridge: Partial<OfflineAuthMethods>) => Promise<T> | undefined,
  fallback: T,
): Promise<T> {
  const bridge = getBridge();
  if (!bridge) return fallback;
  try {
    const result = await invoke(bridge);
    return result === undefined ? fallback : result;
  } catch (error) {
    console.warn(`[offlineSession] ${method} failed over IPC`, error);
    return fallback;
  }
}

export function readSnapshot(
  cognitoSub: string,
): Promise<OfflineSnapshot | null> {
  return call(
    "getOfflineSession",
    (bridge) => bridge.getOfflineSession?.(cognitoSub),
    null,
  );
}

export function writeSnapshot(snapshot: OfflineSnapshot): Promise<boolean> {
  return call(
    "setOfflineSession",
    (bridge) => bridge.setOfflineSession?.(snapshot),
    false,
  );
}

export function listOfflineUsers(): Promise<OfflineUserSummary[]> {
  return call<OfflineUserSummary[]>(
    "listOfflineUsers",
    (bridge) => bridge.listOfflineUsers?.(),
    [],
  );
}

export async function clearSnapshot(cognitoSub: string): Promise<void> {
  await call<void>(
    "clearOfflineSession",
    (bridge) => bridge.clearOfflineSession?.(cognitoSub),
    undefined,
  );
}

export function isOfflineStorageAvailable(): Promise<boolean> {
  return call(
    "isOfflineStorageAvailable",
    (bridge) => bridge.isOfflineStorageAvailable?.(),
    false,
  );
}

// Cognito cannot be asked offline, so the budget is the expiry recorded at the
// last online login. Strict, so a snapshot at exactly its expiry is spent.
export function isSnapshotUsable(
  snapshot: OfflineSnapshot,
  now: number = Date.now(),
): boolean {
  return now < snapshot.refreshTokenExpiresAt;
}

export function isSnapshotFresh(
  snapshot: OfflineSnapshot,
  now: number = Date.now(),
): boolean {
  return now - snapshot.savedAt < SNAPSHOT_FRESH_MS;
}
